// src/theme/index.js - Light, Dark or Auto for Shift (v136.0). Bundled to dist/theme.js (global Theme), loaded straight
// after dist/i18n.js in index.html, admin.html, chat.html and scan.html, BEFORE anything is drawn, so a device set to
// Dark never flashes white. Never in dev.html.
//
// Matt, 16 Oct 2026: "do dark mode overnight, default light, phones and tills". So:
//   - every device starts in Light; nothing changes until somebody chooses
//   - the choice is the DEVICE's (localStorage shift_theme), like the language: a shared till's choice is everyone's
//   - Auto follows the phone or till's own setting, and changes with it
//   - Head Office's switch (app_config theme_dark_enabled, off by default) shows the button; staging always shows it.
//     Off returns every device to Light. A switch that cannot be READ (offline, a blip) changes nothing.
//   - printing is always Light
//
// HOW: the page's colours are CSS variables (shift-ui.css and each page's :root). This stamps data-theme="light" or
// "dark" on <html> (always one of the two, so a page that also follows prefers-color-scheme, like scan.html, obeys the
// choice), and shift-ui.css redefines the variables under :root[data-theme="dark"]. Nothing else in the app knows.

export const THEME_KEY = 'shift_theme';
export const SWITCH_KEY = 'theme_dark_enabled';   // app_config: Head Office shows the button to stores (off by default)
const CHOICES = ['light', 'dark', 'auto'];

let _choice = 'light';
let _allowed = false;
let _mq = null;
let _printing = false;

function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function lsSet(k, v) { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (e) { /* private window: fine */ } }

// ── pure ──────────────────────────────────────────────────────────────────────
export function normChoice(v) { return CHOICES.indexOf(v) >= 0 ? v : 'light'; }
// What the page shows for a choice: auto asks the system (true = the system prefers dark).
export function resolve(choice, systemDark) {
  const c = normChoice(choice);
  if (c === 'auto') return systemDark ? 'dark' : 'light';
  return c;
}
// The next choice when the button is tapped: Light, Dark, Auto, then round again.
export function nextChoice(choice) { return CHOICES[(CHOICES.indexOf(normChoice(choice)) + 1) % CHOICES.length]; }
export function isStagingHost(host) { return /staging|localhost|127\.0\.0\.1/.test(String(host || '')); }

// ── the page ──────────────────────────────────────────────────────────────────
function systemDark() {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches); } catch (e) { return false; }
}
export function current() { return (_printing || !_allowed) ? 'light' : resolve(_choice, systemDark()); }
export function getChoice() { return _choice; }

export function apply() {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const shown = current();
  const root = document.documentElement;
  root.setAttribute('data-theme', shown);
  root.style.colorScheme = shown;   // native controls, scrollbars and form fields follow
  syncButtons();
}

export function setChoice(choice, opts) {
  _choice = normChoice(choice);
  if (!(opts && opts.noSave)) lsSet(THEME_KEY, _choice === 'light' ? null : _choice);
  apply();
  return _choice;
}
export function cycle() { return setChoice(nextChoice(_choice)); }

// The button. Any element marked data-theme-picker becomes one; it shows only when the switch allows dark mode.
const ICON = { light: '☀️', dark: '🌙', auto: '🌓' };
const LABEL = { light: 'Light', dark: 'Dark', auto: 'Auto' };
export function buttonHtml(choice) {
  const c = normChoice(choice);
  // The label and title go through the translator like any other text (the button is not data-no-i18n).
  return '<button type="button" class="theme-btn" data-theme-choice="' + c + '" onclick="Theme.cycle()" title="' +
    LABEL[c] + '" aria-label="' + LABEL[c] + '"><span aria-hidden="true">' + ICON[c] + '</span><span class="theme-btn-label">' +
    LABEL[c] + '</span></button>';
}
export function syncButtons() {
  if (typeof document === 'undefined') return;
  const slots = document.querySelectorAll('[data-theme-picker]');
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i];
    if (!_allowed) { s.innerHTML = ''; s.hidden = true; continue; }
    s.hidden = false;
    const b = s.querySelector('button.theme-btn');
    if (!b || b.getAttribute('data-theme-choice') !== _choice) s.innerHTML = buttonHtml(_choice);
  }
}

// Turn the button on or off. Off returns a device that had chosen Dark or Auto to Light, and forgets the choice.
export function allow(on) {
  _allowed = !!on;
  if (!_allowed && _choice !== 'light') { setChoice('light'); return; }
  apply();
}

// true or false only when the Worker ANSWERED; null when it could not be asked (offline, a blip, no db.js on the page).
export async function readSwitch(fetchImpl) {
  try {
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    if (!base) return null;
    const r = await (fetchImpl || fetch)(base + '/api/config?key=' + SWITCH_KEY, { cache: 'no-store' });
    if (!r.ok) return null;
    const d = await r.json();
    if (!d || typeof d !== 'object') return null;
    return d.value === true || d.value === 'true';
  } catch (e) { return null; }
}
// Unknown keeps the device as it is (a dark device keeps its button, so it can still change back).
export function applySwitch(on) {
  if (on === null || on === undefined) { if (_choice !== 'light') allow(true); return; }
  allow(on);
}

// Start: apply the saved choice at once (before the page paints), then confirm the switch.
export function boot() {
  if (typeof document === 'undefined') return;
  let host = '';
  try { host = location.hostname; } catch (e) { /* no location */ }
  _choice = normChoice(lsGet(THEME_KEY));
  // Before the switch is known, a saved Dark or Auto is honoured (it was allowed when chosen), so there is no flash.
  _allowed = _choice !== 'light';
  apply();
  try {
    _mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (_mq) {
      const onChange = function () { if (_choice === 'auto') apply(); };
      if (_mq.addEventListener) _mq.addEventListener('change', onChange); else if (_mq.addListener) _mq.addListener(onChange);
    }
    window.addEventListener('beforeprint', function () { _printing = true; apply(); });
    window.addEventListener('afterprint', function () { _printing = false; apply(); });
  } catch (e) { /* old browser: Light and Dark still work */ }
  const later = function () {
    if (isStagingHost(host)) { allow(true); return; }
    readSwitch().then(applySwitch);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', later); else later();
}

const api = { setChoice, getChoice, cycle, current, allow, apply, boot, THEME_KEY, SWITCH_KEY };
if (typeof window !== 'undefined') {
  window.Theme = api;   // (esbuild's globalName then sets Theme to this module's exports: the same functions)
  boot();
}
export default api;
